import CONFIG from './config'

class APIClient {
  constructor(baseURL) {
    this.baseURL = baseURL || CONFIG.API_BASE_URL
    this.abortControllers = new Map()
    this.cache = new Map()
  }

  getToken() {
    return localStorage.getItem("jwt_token")
  }

  setToken(token) {
    localStorage.setItem("jwt_token", token)
  }

  clearToken() {
    localStorage.removeItem("jwt_token")
  }

  async fetchWithRetry(url, options = {}, retries = CONFIG.MAX_RETRIES) {
    for (let i = 0; i <= retries; i++) {
      try {
        const response = await fetch(url, options)

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Resource not found')
          }
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        return await response.json()
      } catch (error) {
        if (error.name === 'AbortError') throw error
        if (i === retries) throw error
        await new Promise(resolve => setTimeout(resolve, CONFIG.RETRY_DELAY * (i + 1)))
      }
    }
  }

  async fetchA(url, options = {}) {
    try {
      const response = await fetch(url, options)

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Resource not found')
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      return await response.json()
    } catch (error) {
      if (error.name === 'AbortError') throw error
      throw error
    }
  }

  // ------------------------------------------------
  // AUTHORIZED FETCH (AUTOMATIC JWT)
  // ------------------------------------------------
  async authorizedFetch(url, options = {}) {
    const token = this.getToken()
    const headers = options.headers || {}

    if (token) {
      headers["Authorization"] = `Bearer ${token}`
    }

    return this.fetchA(url, { ...options, headers })
  }

  // ------------------------------------------------
  // CANCEL REQUEST BY KEY
  // ------------------------------------------------
  cancelRequest(key) {
    if (this.abortControllers.has(key)) {
      this.abortControllers.get(key).abort()
      this.abortControllers.delete(key)
    }
  }

  // ------------------------------------------------
  // FETCH LATEST LOGS
  // ------------------------------------------------
  async fetchLatestLogs() {
    try {
      return await this.authorizedFetch(`${this.baseURL}/logs/latest`)
    } catch (error) {
      console.error('Failed to fetch latest logs:', error)
      throw error
    }
  }

  // ------------------------------------------------
  // FETCH SESSIONS (with pagination)
  // ------------------------------------------------
  async fetchSessions(page = 0, limit = 50, useCache = true) {
    const cacheKey = `sessions-${page}-${limit}`

    if (useCache && this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey)
      if (Date.now() - cached.timestamp < 3000) {
        return cached.data
      }
    }

    try {
      const params = new URLSearchParams({ page, limit })
      const data = await this.authorizedFetch(`${this.baseURL}/sessions?${params}`)
      
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid sessions response')
      }
      
      const sessions = Array.isArray(data) ? data : (data.sessions || [])
      const result = {
        sessions: Array.isArray(sessions) ? sessions : [],
        pagination: data.pagination || { page, limit, totalSessions: sessions.length, estimatedTotal: 0 }
      }
      
      this.cache.set(cacheKey, { data: result, timestamp: Date.now() })
      return result
    } catch (error) {
      console.error('Failed to fetch sessions:', error)
      throw error
    }
  }

  async fetchSessionLogs(sessionId, filters = {}) {
    const { lastId, afterId, level, startTime, endTime, limit = 150 } = filters;
    const requestKey = `session-${sessionId}-${lastId || ''}-${afterId || ''}`;
    
    // Do not cancel fast background polling requests
    if (!afterId) {
      this.cancelRequest(requestKey);
    }

    const controller = new AbortController();
    this.abortControllers.set(requestKey, controller);

    try {
      const params = new URLSearchParams();
      if (lastId !== undefined && lastId !== null) params.append('lastId', lastId);
      if (afterId !== undefined && afterId !== null) params.append('afterId', afterId);
      if (level) params.append('level', level);
      if (startTime) params.append('startTime', startTime);
      if (endTime) params.append('endTime', endTime);
      params.append('limit', limit);

      const data = await this.authorizedFetch(
        `${this.baseURL}/logs/session/${encodeURIComponent(sessionId)}?${params}`,
        { signal: controller.signal }
      )
      this.abortControllers.delete(requestKey);
      
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid session logs response');
      }
      
      const logs = Array.isArray(data.logs) ? data.logs : [];
      const result = {
        logs,
        hasMore: !!data.hasMore
      };
      
      return result;
    } catch (error) {
      this.abortControllers.delete(requestKey);
      if (error.name === 'AbortError') {
        return null;
      }
      console.error(`Failed to fetch logs for session ${sessionId}:`, error);
      throw error;
    }
  }

  // ------------------------------------------------
  // REAL-TIME STREAMING (WebSocket & SSE)
  // ------------------------------------------------
  getWsUrl(sessionId) {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const host = window.location.host
    const token = this.getToken() || ''
    const sId = encodeURIComponent(sessionId || '')
    return `${protocol}//${host}/ws/logs?token=${encodeURIComponent(token)}&sessionId=${sId}`
  }

  connectStream(sessionId, onLogs, onStatusChange) {
    if (!sessionId) return () => {}

    let ws = null
    let es = null
    let isCleanedUp = false
    const token = this.getToken()

    // 1. Try WebSocket first (fastest, sub-20ms)
    try {
      const wsUrl = this.getWsUrl(sessionId)
      ws = new WebSocket(wsUrl)

      ws.onopen = () => {
        if (isCleanedUp) {
          ws.close()
          return
        }
        onStatusChange?.({ connected: true, mode: 'websocket' })
        ws.send(JSON.stringify({ action: 'subscribe', sessionId }))
      }

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          if (data.type === 'logs' && Array.isArray(data.logs) && data.sessionId === sessionId) {
            onLogs(data.logs)
          }
        } catch {
          // Ignored
        }
      }

      ws.onerror = () => {
        // Fall back to SSE on WS error
        if (!isCleanedUp && !es) {
          startSSE()
        }
      }

      ws.onclose = () => {
        if (!isCleanedUp && !es) {
          startSSE()
        }
      }
    } catch {
      startSSE()
    }

    // 2. Fallback to Server-Sent Events (SSE)
    function startSSE() {
      if (isCleanedUp || es) return
      try {
        const streamUrl = `${CONFIG.API_BASE_URL}/logs/stream/${encodeURIComponent(sessionId)}?token=${encodeURIComponent(token || '')}`
        es = new EventSource(streamUrl)

        es.onopen = () => {
          onStatusChange?.({ connected: true, mode: 'sse' })
        }

        es.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data)
            if (data.type === 'logs' && Array.isArray(data.logs) && data.sessionId === sessionId) {
              onLogs(data.logs)
            }
          } catch {
            // Ignored
          }
        }

        es.onerror = () => {
          onStatusChange?.({ connected: false, mode: 'polling' })
        }
      } catch {
        onStatusChange?.({ connected: false, mode: 'polling' })
      }
    }

    // Return cleanup function
    return () => {
      isCleanedUp = true
      if (ws) {
        try {
          ws.close()
        } catch {
          // Ignored
        }
        ws = null
      }
      if (es) {
        try {
          es.close()
        } catch {
          // Ignored
        }
        es = null
      }
    }
  }

  // ------------------------------------------------
  // DELETE SESSION
  // ------------------------------------------------
  async deleteSession(sessionId) {
    try {
      const data = await this.authorizedFetch(
        `${this.baseURL}/sessions/${encodeURIComponent(sessionId)}`,
        { method: 'DELETE' }
      )
      this.cache.clear()
      return data
    } catch (error) {
      console.error(`Failed to delete session ${sessionId}:`, error)
      throw error
    }
  }

  // ------------------------------------------------
  // DELETE MULTIPLE SESSIONS (BATCH)
  // ------------------------------------------------
  async deleteSessions(sessionIds) {
    if (!Array.isArray(sessionIds) || sessionIds.length === 0) return null
    try {
      const data = await this.authorizedFetch(
        `${this.baseURL}/sessions/batch-delete`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionIds })
        }
      )
      this.cache.clear()
      return data
    } catch (error) {
      console.error('Failed to batch delete sessions:', error)
      throw error
    }
  }

  // ------------------------------------------------
  // CLEAR CACHE
  // ------------------------------------------------
  clearCache() {
    this.cache.clear()
  }

  // ------------------------------------------------
  // CANCEL ALL REQUESTS
  // ------------------------------------------------
  cancelAllRequests() {
    this.abortControllers.forEach(controller => controller.abort())
    this.abortControllers.clear()
  }

  // ------------------------------------------------
  // ------------------ AUTH ------------------------
  // ------------------------------------------------

  // LOGIN (POST /login)
  async login(username, password) {
    const data = await this.fetchWithRetry(`${this.baseURL}/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password })
    })

    if (!data.token) {
      throw new Error("Login failed")
    }

    this.setToken(data.token)
    return true
  }

  // VALIDATE TOKEN (GET /validate)
  async validate() {
    try {
      return await this.authorizedFetch(`${this.baseURL}/validate`)
    } catch {
      this.clearToken()
      return null
    }
  }
}

export const apiClient = new APIClient()
export default apiClient
